/* Copyright (c) 2022 Xperi. All Rights Reserved. */

/**
 * this file implements all the functions which are exposed from data/domain layer to other layers
 * any modification over the exposed functions can be done here
 */

import { ScreenType } from 'client-code/previews';
import { StationPolicy } from 'client-code/channels-service';
import { BodyInfo } from 'client-code/body-info-service';
import { SubEvent } from 'sub-events';
import {
    DrmType,
    TransportEncodingType,
    VideoProviderConfiguration,
} from 'client-code/video-provider-configurations';
import { Feature, IpOnlyResponse } from 'client-code/fe-fav-service';
import { ActionKernelType, ActionType } from 'client-code/actions';
import { Services } from '../infrastructure/Services';
import { TChannels } from '../types/TChannels';
import { IServices } from '../interfaces/IServices';
import { GridGuideRow } from '../entities/GridGuideRow';
import { Session } from '../entities/Session';
import { TCreateSessionParams } from '../types/TCreateSessionParams';
import { Channel } from '../entities/Channel';
import {
    DEFAULT_RETRY_OPTIONS,
    IDLE_GRID_GUIDE_DURATION,
    MILLISECONDS_IN_A_SECOND,
    TIVO_SERVICES,
} from '../../config';
import { Log } from '../logging/Log';
import { AppUtils } from '../AppUtils';
import { TOfferPreview } from '../types/TOfferPreview';
import { Locale } from '../locale/Locale';
import { LocaleKeys } from '../locale/LocaleKeys';
import { ChannelsType } from '../types/TChannelsType';
import { TAiringOffer } from '../types/TAiringOffer';
import { GuideCell } from '../entities/GuideCell';
import { BrandingUiInfoKeys } from '../entities/BrandingUiInfoKeys';
import { TServicesParams } from '../types/TServicesParams';
import { SlsServiceEndpointsSearchService } from '../infrastructure/SlsServiceEndpointsSearchServiceRepository';
import { RetryMechanism } from '../retrymechanism/RetryMechanism';
import { RetryOptions } from '../retrymechanism/RetryOptions';
import { TInHomeLocation } from '../types/TInHomeLocation';
import { RecoveryMechanism } from '../recoverymechanism/RecoveryMechanism';
import {
    EmptyResponseError,
    ErrorTypes,
    OfferActionDetailsError,
    PartialResponseError,
    TypedChannelsNotFoundError,
    OfferActionError,
} from '../error';
import { DeviceModel } from '../device/DeviceModel';
import {
    ActionDetailsService,
    AiringOfferService,
    ActionsService,
    BodyInfoService,
    BrandingUIBundleService,
    ChannelsService,
    FeFavFilterService,
    GridGuideService,
    InHomeLocationService,
    InternalRatingsService,
    MyWanIpService,
    PreviewsService,
    SessionManagerService,
    VodSocuLastViewedPositionService,
    StationPolicyService,
    VPConfigurationsService,
} from '../infrastructure';
import { Branding } from '../branding/Branding';
import {
    TActionList,
    TActionsParams,
    TActionDetailsList,
    TActionDetailsParams,
    TIpAddress,
    TSocuLastViewedPositionParams,
    TSession,
    TSessionState,
    TOfferPreviewCustomData,
} from '../types';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';
import { BrandingUiUtility } from '../utils/BrandingUiUtility';
import { IPlayable } from '../interfaces';
import { PlaybackContentType } from '../socu/enums/PlaybackContentType';

export class ServicesClientImpl implements IServices {
    private static servicesBaseUrls: { [key: string]: string } = {};

    public static inHomeLocationDataUpdateEvent: SubEvent<boolean> = new SubEvent();

    private static TAG = 'ServicesClientImpl';

    private isSlsServiceEndpointResIncomplete = (slsEndpoints: { [key: string]: string }) => {
        const services = Object.keys(slsEndpoints);
        const postSigninServices = Object.values(TIVO_SERVICES);
        for (let i = 0; i < postSigninServices.length; i += 1) {
            if (!services.includes(postSigninServices[i])) {
                return true;
            }
        }
        return false;
    };

    private validateSlsServiceResponse = (slsResponse: { [key: string]: string }) => {
        if (!Object.keys(slsResponse).length) {
            throw new EmptyResponseError(
                ErrorTypes.SLS_SE_EMPTY_RESPONSE_C806,
                'Received empty response from SlsServiceEndpointGet',
                this.formatErrorDescription(
                    Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION),
                ),
            );
        }

        if (this.isSlsServiceEndpointResIncomplete(slsResponse)) {
            throw new PartialResponseError(
                ErrorTypes.SLS_SE_PARTIAL_RESPONSE_C805,
                'Received partial response from SlsServiceEndpointGet',
                this.formatErrorDescription(
                    Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION),
                ),
            );
        }
    };

    // TODO: move this method some better place.
    private formatErrorDescription = (description: string) => {
        let formattedDescription = description;
        formattedDescription = formattedDescription.replace(
            'PHONE_CUSTOMER_SUPPORT_NUMBER',
            this.getBrandingUiInfoForKey(BrandingUiInfoKeys.PHONE_CUSTOMER_SUPPORT_NUMBER),
        );
        formattedDescription = formattedDescription.replace(
            'CUSTOMER_SUPPORT_NAME',
            this.getBrandingUiInfoForKey(BrandingUiInfoKeys.CUSTOMER_SUPPORT_NAME),
        );
        return formattedDescription;
    };

    private formatSocuErrorDescription = (description: string) => {
        const replaceableValues = {
            PHONE_CUSTOMER_SUPPORT_NUMBER: this.getBrandingUiInfoForKey(
                BrandingUiInfoKeys.PHONE_CUSTOMER_SUPPORT_NUMBER,
            ),
            CUSTOMER_SUPPORT_NAME: this.getBrandingUiInfoForKey(
                BrandingUiInfoKeys.CUSTOMER_SUPPORT_NAME,
            ),

            URL_ERROR: this.getBrandingUiInfoForKey(BrandingUiInfoKeys.URL_ERROR),
        };
        return BrandingUiUtility.formatDescription(description, replaceableValues);
    };

    private _fetchDeviceFeatures = () => {
        return Services.getInstanceOf(FeFavFilterService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.FeFavFilterService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).fetchDeviceFeaturesFromService();
    };

    private _fetchSlsServiceEndpoints = (): Promise<void> => {
        return new Promise((resolve, reject) => {
            new SlsServiceEndpointsSearchService(DeviceModel.getSlsBaseUrl())
                .fetchSlsServiceEndpoints()
                .then((response) => {
                    this.validateSlsServiceResponse(response);
                    ServicesClientImpl.servicesBaseUrls = response;
                    resolve();
                })
                .catch((error) => {
                    Log.e(ServicesClientImpl.TAG, 'Failed to get SLS service endpoints.');
                    Log.d(ServicesClientImpl.TAG, error);
                    reject(error);
                });
        });
    };

    private _validateOfferActionServiceResponse = (data: TActionList) => {
        const { actionList } = data;
        if (!actionList || !Object.keys(actionList).length) {
            throw new EmptyResponseError(
                ErrorTypes.OFFER_ACTION_EMPTY_RESPONSE_ERROR_V203,
                'empty response from v1/offer/action api',
                this.formatSocuErrorDescription(
                    Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_DESCRIPTION),
                ),
                Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_TITLE),
            );
        }
        const watchFromProviderAction = actionList.actions.find((action) => {
            return (
                action.actionType === ActionType.WatchFromProviderAction &&
                action.kernel?.actionKernelType === ActionKernelType.WatchFromSocuActionKernel
            );
        });
        if (!watchFromProviderAction) {
            throw new PartialResponseError(
                ErrorTypes.SOCU_PROVIDER_NOT_FOUND_V290,
                'socu provider not found in action api response',
                this.formatSocuErrorDescription(
                    Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_DESCRIPTION),
                ),
                Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_TITLE),
            );
        }
    };

    private _validateActionDetailsServiceResponse = (data: TActionDetailsList) => {
        const { actionDetails } = data;
        if (!actionDetails || !Object.keys(actionDetails).length) {
            throw new EmptyResponseError(
                ErrorTypes.OTT_OFFER_ACTION_DETAILS_EMPTY_RESPONSE_ERROR_V306,
                'fetchOfferActionDetails empty response',
                this.formatSocuErrorDescription(
                    Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_DESCRIPTION),
                ),
                Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_TITLE),
            );
        }
        if (!actionDetails?.watchActionsList[0]?.kernel?.partnerId) {
            throw new PartialResponseError(
                ErrorTypes.SOCU_OFFER_PARTNER_ID_ERROR_V393,
                'The configured PartnerId for a specific offer is missing',
                this.formatSocuErrorDescription(
                    Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_DESCRIPTION),
                ),
                Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_TITLE),
            );
        }
    };

    loadSlsServiceEndpoints = (): Promise<TServicesParams> => {
        const _getServiceParams = this._fetchSlsServiceEndpoints.bind(this);
        return new Promise((resolve, reject) => {
            RetryMechanism.callServiceWithRetry(
                _getServiceParams,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then((response) => {
                    resolve(<TServicesParams>response);
                })
                .catch((retryError) => {
                    reject(retryError);
                });
        });
    };

    waitForSlsServiceEndpoints = (): Promise<TServicesParams> => {
        const _getServiceParams = this._fetchSlsServiceEndpoints.bind(this);
        return new Promise((resolve, reject) => {
            RecoveryMechanism.callServiceWithRecovery(_getServiceParams, [])
                .then((response) => {
                    resolve(<TServicesParams>response);
                })
                .catch((error) => {
                    if (
                        error instanceof PartialResponseError ||
                        error instanceof EmptyResponseError
                    ) {
                        resolve(this.waitForSlsServiceEndpoints());
                    } else reject(error);
                });
        });
    };

    fetchVPConfigurations = (
        supportedDrmType: Array<DrmType>,
        supportedTransportEncodingType: Array<TransportEncodingType>,
    ): Promise<void> => {
        return Services.getInstanceOf(VPConfigurationsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.VPConfigurationsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).fetchVPConfigurations(supportedDrmType, supportedTransportEncodingType);
    };

    loadVpConfigurations = (): Promise<boolean> => {
        const supportedDrmTypes: DrmType[] = [DrmType.Widevine];
        const supportedTransportEncodingTypes: TransportEncodingType[] = [
            // if we need to support multiple TransportStream, add below
            // and same need to be handled in VideoPlayerModelImpl for DRM
            TransportEncodingType.DashFmp4TransportStream,
        ];
        const fetchVPConfigurations = this.fetchVPConfigurations.bind(this);
        return new Promise((resolve, reject) => {
            RetryMechanism.callServiceWithRetry(
                fetchVPConfigurations,
                [supportedDrmTypes, supportedTransportEncodingTypes],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then(() => {
                    resolve(true);
                })
                .catch((error: any) => {
                    reject(error);
                });
        });
    };

    fetchVPConfigurationsForVideoPlayer = (
        linearProviderPartnerId: string,
        forceNetworkCall?: boolean,
    ): Promise<Array<VideoProviderConfiguration>> => {
        return Services.getInstanceOf(VPConfigurationsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.VPConfigurationsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).fetchVPConfigurationsForVideoPlayer(linearProviderPartnerId, forceNetworkCall);
    };

    fetchStationPolicies = (): Promise<Array<StationPolicy>> => {
        return Services.getInstanceOf(StationPolicyService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).fetchStationPolicies();
    };

    fetchAiringOffers = (
        stationId: string,
        networkCallId?: number,
        playerCurrentTime?: number,
    ): Promise<TAiringOffer> => {
        return Services.getInstanceOf(AiringOfferService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.AiringOffersService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).fetchAiringOffers(stationId, networkCallId, playerCurrentTime);
    };

    fetchMyChannels = (offset?: number, limit?: number): Promise<TChannels> => {
        return Services.getInstanceOf(ChannelsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            channelLogoBaseUrl:
                ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelLogoImageHost],
        }).fetchMyChannels(offset, limit);
    };

    fetchAllChannels = (offset?: number, limit?: number): Promise<TChannels> => {
        return Services.getInstanceOf(ChannelsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            channelLogoBaseUrl:
                ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelLogoImageHost],
        }).fetchAllChannels(offset, limit);
    };

    loadChannelsInfo = (): Promise<boolean> => {
        const fetchAllChannels = this.fetchAllChannels.bind(this);
        return new Promise((resolve, reject) => {
            RetryMechanism.callServiceWithRetry(
                fetchAllChannels,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then(() => {
                    resolve(true);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    fetchChannels = async (
        channelType?: string,
        offset?: number,
        limit?: number,
    ): Promise<TChannels> => {
        const service = Services.getInstanceOf(ChannelsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            channelLogoBaseUrl:
                ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelLogoImageHost],
        });
        let data: TChannels;
        if (channelType === Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_MY_CHANNELS))
            data = await service.fetchMyChannels(offset, limit);
        else data = await service.fetchAllChannels(offset, limit);

        if (!data.channels.length)
            throw new TypedChannelsNotFoundError(
                ErrorTypes.GUIDE_CHANNELS_SERVICE_EMPTY_RESPONSE_C1106,
                this.formatErrorDescription(
                    Locale.getString(LocaleKeys.GUIDE_TEMPORARY_GUIDE_ISSUE_OVERLAY_DESCRIPTION),
                ),
            );
        return data;
    };

    fetchProgramMetadata = (
        stationId: string[],
        startTime: Date,
        endTime: Date,
    ): Promise<GridGuideRow[]> => {
        return Services.getInstanceOf(GridGuideService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.GridGuideService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            imageHostBaseUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
        }).fetchProgramMetadata(stationId, startTime, endTime);
    };

    private static _createDummyGuideRowData = (
        channel: Channel,
        currentTimestamp: Date,
        nextTimestamp: Date,
    ) => {
        const { stationId, affiliate, callSign, videoResolution } = channel;
        const guideRow = {
            stationId,
            guideCells: [
                {
                    title: `${Locale.getString(LocaleKeys.GUIDE_SELECT_TO_WATCH_TEXT)} ${
                        affiliate?.split(' ')[0] || callSign
                    } ${videoResolution?.toUpperCase()}`,
                    startTime: currentTimestamp,
                    duration:
                        (nextTimestamp.getTime() - currentTimestamp.getTime()) /
                        MILLISECONDS_IN_A_SECOND,
                },
                {},
            ],
        };
        return guideRow;
    };

    private _createDummyGuideCell = (
        startTime: Date,
        endTime: Date,
        title: string = Locale.getString(LocaleKeys.LIVE_TV_FULL_INFO_BANNER_TITLE_NOT_AVAILABLE),
    ): GuideCell => {
        const duration: number =
            (endTime?.getTime() - startTime?.getTime()) / MILLISECONDS_IN_A_SECOND;
        return {
            collectionId: '',
            contentId: '',
            duration,
            offerId: '',
            posterImageUrl: '',
            startTime,
            title,
        };
    };

    private _validateGuideRows = (
        guideRows: GridGuideRow[],
        stationIds: string[],
        startTime: Date,
        endTime: Date,
    ): GridGuideRow[] => {
        const mGuideRows = guideRows.map((guideRow, index) => {
            if (!guideRow || guideRow.guideCells.length === 0) {
                return <GridGuideRow>{
                    stationId: stationIds[index],
                    guideCells: [this._createDummyGuideCell(startTime, endTime)],
                };
            }
            const mGuideCells: GuideCell[] = [];
            guideRow.guideCells.forEach((guideCell, idx): void => {
                const nextGuideCell = guideRow.guideCells[idx + 1];
                const currentGuideCellEndTime: Date = AppUtils.addSecondsToDateTime(
                    guideCell?.startTime,
                    guideCell?.duration,
                );
                if (idx === 0 && guideCell.startTime.getTime() > startTime.getTime()) {
                    mGuideCells.push(this._createDummyGuideCell(startTime, guideCell.startTime));
                    mGuideCells.push(guideCell);
                    if (
                        guideRow.guideCells.length === idx + 1 &&
                        currentGuideCellEndTime.getTime() < endTime.getTime()
                    ) {
                        mGuideCells.push(
                            this._createDummyGuideCell(currentGuideCellEndTime, endTime),
                        );
                    }
                } else if (
                    nextGuideCell &&
                    currentGuideCellEndTime.getTime() < nextGuideCell.startTime.getTime()
                ) {
                    mGuideCells.push(guideCell);
                    mGuideCells.push(
                        this._createDummyGuideCell(
                            currentGuideCellEndTime,
                            nextGuideCell.startTime,
                        ),
                    );
                } else if (
                    guideRow.guideCells.length === idx + 1 &&
                    currentGuideCellEndTime.getTime() < endTime.getTime()
                ) {
                    mGuideCells.push(guideCell);
                    mGuideCells.push(this._createDummyGuideCell(currentGuideCellEndTime, endTime));
                } else {
                    mGuideCells.push(guideCell);
                }
            });
            return {
                stationId: stationIds[index],
                guideCells: mGuideCells,
            };
        });

        return mGuideRows;
    };

    fetchGuideRowsData = (
        channels: Channel[],
        startTime: Date,
        endTime: Date,
        guideStartTime?: Date,
    ): Promise<GridGuideRow[]> => {
        const stationIds: string[] = channels.map(({ stationId = '' }) => {
            return stationId;
        });
        const _guideStartTime = guideStartTime || new Date();

        return new Promise((resolve, reject) => {
            this.fetchProgramMetadata(stationIds, startTime, endTime)
                .then((guideRows) => {
                    const mGuideRows = this._validateGuideRows(
                        guideRows,
                        stationIds,
                        startTime,
                        endTime,
                    );
                    resolve(mGuideRows);
                })
                .catch((error) => {
                    Log.e(ServicesClientImpl.TAG, error);

                    const dummyGuideRows = channels.map((channel) => {
                        return ServicesClientImpl._createDummyGuideRowData(
                            channel,
                            _guideStartTime,
                            AppUtils.addMinutesToDateTime(
                                _guideStartTime,
                                IDLE_GRID_GUIDE_DURATION,
                            ),
                        );
                    });
                    const modifiedError = { ...error, dummyGuideRows };
                    reject(modifiedError);
                });
        });
    };

    fetchOfferPreview = (
        offerId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> => {
        return Services.getInstanceOf(PreviewsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.PreviewsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            imageHostBaseUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
        }).fetchOfferPreview(offerId, screenType, networkCallId);
    };

    fetchVodOfferPreview = (
        vodOfferId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> => {
        return Services.getInstanceOf(PreviewsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.PreviewsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).fetchVodOfferPreview(vodOfferId, screenType, networkCallId);
    };

    getStandardBannerVodOfferPreviewByOfferId = (offerId: string): Promise<TOfferPreview> => {
        return this.fetchVodOfferPreview(
            offerId,
            ScreenType.StandardBanner,
            UniqueIdentifier.getNetworkCallId(),
        );
    };

    getFullBannerVodOfferPreviewByOfferId = (offerId: string): Promise<TOfferPreview> => {
        return this.fetchVodOfferPreview(
            offerId,
            ScreenType.FullBanner,
            UniqueIdentifier.getNetworkCallId(),
        );
    };

    getCompleteVodOfferPreviewData = async (
        offerId: string,
        standardBannerResponse: TOfferPreviewCustomData,
        screenType: ScreenType,
    ): Promise<TOfferPreviewCustomData> => {
        if (screenType === ScreenType.StandardBanner) {
            return (await this.getStandardBannerVodOfferPreviewByOfferId(offerId)).offerPreview;
        }
        const fullBannerResponse = await this.getFullBannerVodOfferPreviewByOfferId(offerId);
        return AppUtils.mergeObjects([standardBannerResponse, fullBannerResponse.offerPreview]);
    };

    fetchOfferActions = (actionsParams: TActionsParams): Promise<TActionList> => {
        return new Promise((resolve, reject) => {
            const offerActionsService = Services.getInstanceOf(ActionsService, {
                accessToken: DeviceModel.getAccessToken(),
                basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ActionsService],
                bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            });
            offerActionsService
                .fetchOfferActions(actionsParams)
                .then((data) => {
                    this._validateOfferActionServiceResponse(data);
                    resolve(data);
                })
                .catch((error: any) => {
                    let offerActionError = error;
                    if (
                        !(
                            error instanceof EmptyResponseError ||
                            error instanceof PartialResponseError
                        )
                    ) {
                        const offerActionServiceError = error?.response?.data;
                        offerActionError = new OfferActionError(
                            offerActionServiceError?.message,
                            offerActionServiceError?.code,
                            this.formatSocuErrorDescription(
                                Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_DESCRIPTION),
                            ),
                        );
                    }
                    reject(offerActionError);
                });
        });
    };

    createSession = (
        sessionParams: TCreateSessionParams,
        sessionNetworkCallId?: number,
    ): Promise<TSession> => {
        return Services.getInstanceOf(SessionManagerService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.SessionManagerService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).createSession(sessionParams, sessionNetworkCallId);
    };

    sessionKeepAlive = (
        session: Session,
        sessionNetworkCallId?: number,
    ): Promise<TSessionState> => {
        return Services.getInstanceOf(SessionManagerService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.SessionManagerService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).sessionKeepAlive(session, sessionNetworkCallId);
    };

    sessionDelete = (session: Session, sessionNetworkCallId?: number): Promise<TSessionState> => {
        return Services.getInstanceOf(SessionManagerService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.SessionManagerService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).sessionDelete(session, sessionNetworkCallId);
    };

    getNextChannel = (channelNumber: string): Promise<Channel> => {
        return Services.getInstanceOf(ChannelsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            channelLogoBaseUrl:
                ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelLogoImageHost],
        }).getNextChannel(channelNumber);
    };

    getPreviousChannel = (channelNumber: string): Promise<Channel> => {
        return Services.getInstanceOf(ChannelsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            channelLogoBaseUrl:
                ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelLogoImageHost],
        }).getPreviousChannel(channelNumber);
    };

    tuneToChannel = (channelNumber: string): Promise<Channel> => {
        return Services.getInstanceOf(ChannelsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            channelLogoBaseUrl:
                ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelLogoImageHost],
        }).tuneToChannel(channelNumber);
    };

    fetchAndStoreInternalRatingsData = (): Promise<void> => {
        return Services.getInstanceOf(InternalRatingsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.InternalRatingsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).fetchAndStoreInternalRatingsData();
    };

    recoverInternalRatingsInBackground = () => {
        const fetchAndStoreInternalRatingsData = this.fetchAndStoreInternalRatingsData.bind(this);
        RecoveryMechanism.callServiceWithRecovery(fetchAndStoreInternalRatingsData, []);
    };

    loadInternalRatings = (): Promise<boolean> => {
        const fetchAndStoreInternalRatingsData = this.fetchAndStoreInternalRatingsData.bind(this);
        return new Promise((resolve, reject) => {
            RetryMechanism.callServiceWithRetry(
                fetchAndStoreInternalRatingsData,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then(() => {
                    resolve(true);
                })
                .catch((error: any) => {
                    reject(error);
                });
        });
    };

    getLastPlayedOrFallbackToFirstByChannelNumber = async (
        channelNumber: string | null,
    ): Promise<IPlayable> => {
        let channel: Channel;
        try {
            if (channelNumber === null) throw new Error('Last tuned channel not found.');
            channel = await new ServicesClientImpl().tuneToChannel(channelNumber);
        } catch (error) {
            const channels: TChannels = await this.fetchMyChannels(0, 1);
            channel = channels?.channels[0];
        }
        return { channel, playbackContentType: PlaybackContentType.LIVE };
    };

    getInternalRatingName = (ratingKey: string): string => {
        return Services.getInstanceOf(InternalRatingsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.InternalRatingsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).getInternalRatingName(ratingKey);
    };

    fetchBodyInfo = (): BodyInfo => {
        return Services.getInstanceOf(BodyInfoService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.BodyInfoService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).fetchBodyInfo();
    };

    fetchAndStoreBodyInfoData = (): Promise<BodyInfo> => {
        return Services.getInstanceOf(BodyInfoService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.BodyInfoService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).fetchAndStoreBodyInfoData();
    };

    loadBodyInfo = (): Promise<boolean> => {
        const fetchAndStoreBodyInfoData = this.fetchAndStoreBodyInfoData.bind(this);
        return new Promise((resolve, reject) => {
            RetryMechanism.callServiceWithRetry(
                fetchAndStoreBodyInfoData,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then(() => {
                    resolve(true);
                })
                .catch((error: any) => {
                    reject(error);
                });
        });
    };

    waitForBodyInfo = (): Promise<boolean> => {
        const fetchAndStoreBodyInfoData = this.fetchAndStoreBodyInfoData.bind(this);
        return new Promise((resolve, reject) => {
            RecoveryMechanism.callServiceWithRecovery(fetchAndStoreBodyInfoData, [])
                .then(() => {
                    resolve(true);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    fetchNextChannelsFromChannelNumber = (
        channelNumber: string,
        channelsType: ChannelsType,
        limit: number,
    ): Promise<TChannels> => {
        const mChannelsType =
            channelsType === Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_ALL_CHANNELS)
                ? ChannelsType.ALL
                : ChannelsType.SUBSCRIBED;
        return Services.getInstanceOf(ChannelsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            channelLogoBaseUrl:
                ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelLogoImageHost],
        }).fetchNextChannelsFromChannelNumber(channelNumber, mChannelsType, limit);
    };

    getInHomeLocation = (): TInHomeLocation => {
        return Services.getInstanceOf(InHomeLocationService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.InHomeLocationService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).getInHomeLocation();
    };

    fetchAndStoreInHomeLocation = (): Promise<TInHomeLocation> => {
        return Services.getInstanceOf(InHomeLocationService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.InHomeLocationService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).fetchAndStoreInHomeLocation();
    };

    loadInHomeLocationInfo = (): Promise<boolean> => {
        const fetchAndStoreInHomeLocation = this.fetchAndStoreInHomeLocation.bind(this);
        return new Promise((resolve, reject) => {
            RetryMechanism.callServiceWithRetry(
                fetchAndStoreInHomeLocation,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then(() => {
                    resolve(true);
                    ServicesClientImpl.inHomeLocationDataUpdateEvent.emit(true);
                })
                .catch((error: any) => {
                    reject(error);
                });
        });
    };

    getBrandingUIBundleInfo = (): Promise<void> => {
        return Services.getInstanceOf(BrandingUIBundleService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.BrandingUIBundleService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            slsDomain: DeviceModel.getTiVoSettings().getSlsDomain(),
        }).getBrandingUiBundleInfo();
    };

    loadBrandingUiBundleInfo = (): Promise<boolean> => {
        const getBrandingUIBundleInfo = this.getBrandingUIBundleInfo.bind(this);
        return new Promise((resolve, reject) => {
            RetryMechanism.callServiceWithRetry(
                getBrandingUIBundleInfo,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then(() => {
                    resolve(true);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    waitForBrandingUiBundleInfo = (): Promise<boolean> => {
        const getBrandingUIBundleInfo = this.getBrandingUIBundleInfo.bind(this);
        return new Promise((resolve) => {
            RecoveryMechanism.callServiceWithRecovery(getBrandingUIBundleInfo, []);
            resolve(true);
        });
    };

    getDeviceFeatureByName = (name: string, defaultValue: Feature): Feature => {
        try {
            return Services.getInstanceOf(FeFavFilterService, {
                accessToken: DeviceModel.getAccessToken(),
                basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.FeFavFilterService],
                bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            }).getDeviceFeatureByName(name);
        } catch (error) {
            Log.e(ServicesClientImpl.TAG, 'getDeviceFeatureByName', error);
            return defaultValue;
        }
    };

    ipOnly = (): Promise<IpOnlyResponse> => {
        return Services.getInstanceOf(FeFavFilterService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.FeFavFilterService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).ipOnly();
    };

    fetchDeviceFeaturesFromService = async () => {
        const _fetchDeviceFeaturesFromService = this._fetchDeviceFeatures.bind(this);
        return new Promise((resolve, reject) => {
            RetryMechanism.callServiceWithRetry(
                _fetchDeviceFeaturesFromService,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then(() => {
                    resolve(true);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    waitForDeviceFeaturesFromService = () => {
        const fetchDeviceFeaturesData = this._fetchDeviceFeatures.bind(this);
        return new Promise((resolve, reject) => {
            RecoveryMechanism.callServiceWithRecovery(fetchDeviceFeaturesData, [])
                .then(() => {
                    resolve(true);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    getBrandingUiInfoForKey = (key: string): any => {
        try {
            return Services.getInstanceOf(BrandingUIBundleService, {
                accessToken: DeviceModel.getAccessToken(),
                basePath:
                    ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.BrandingUIBundleService],
                bodyId: DeviceModel.getTiVoSettings().getBodyId(),
                slsDomain: DeviceModel.getTiVoSettings().getSlsDomain(),
            }).getBrandingUiInfoForKey(key);
        } catch (error) {
            return Branding.getFallbackBrandingUiInfoForKey(key);
        }
    };

    // TODO: This method has to move to brandingUiUtility after removing the cyclic dependency from this file
    getAppUiImageByKey = (key: string): any => {
        const images = this.getBrandingUiInfoForKey(BrandingUiInfoKeys.APP_IMAGES) || [];
        for (let imageObj of images) {
            if (imageObj.imageIcon === key) {
                return imageObj;
            }
        }
        return null;
    };

    getPartnerBrandingImageUrl(width: number, height: number): string {
        const images = this.getBrandingUiInfoForKey(BrandingUiInfoKeys.IMAGE)?.images ?? [];
        const { imageUrl } = images.reduce(
            (bestImage, currentImage) => {
                const currentAspectRatioDiff = Math.abs(
                    currentImage.width / currentImage.height - width / height,
                );
                const scale = Math.max(currentImage.width / width, currentImage.height / height);
                const scaleDiff = Math.abs(scale - 1.0);

                if (currentAspectRatioDiff < bestImage.aspectRatioDiff) {
                    return {
                        imageUrl: currentImage.imageUrl,
                        aspectRatioDiff: currentAspectRatioDiff,
                        scaleDiff,
                    };
                }
                if (
                    currentAspectRatioDiff === bestImage.aspectRatioDiff &&
                    scaleDiff < bestImage.scaleDiff
                ) {
                    return {
                        imageUrl: currentImage.imageUrl,
                        aspectRatioDiff: currentAspectRatioDiff,
                        scaleDiff,
                    };
                }

                return bestImage;
            },
            { imageUrl: '', aspectRatioDiff: Infinity, scaleDiff: Infinity },
        );

        return imageUrl || '';
    }

    // TODO: uncomment in phase 2
    // getPDSnapshots = (): Promise<{ [key: string]: string }> => {
    //     return Services.getInstanceOf(PDSnapshotService, {
    //         accessToken: DeviceModel.getAccessToken(),
    //         basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.PDChangeService],
    //         bodyId: DeviceModel.getTiVoSettings().getBodyId(),
    //     }).getPDSnapshots();
    // };

    getMyWanIpAddress = (): Promise<TIpAddress> => {
        return Services.getInstanceOf(MyWanIpService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.MyWanIpService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).getMyWanIpAddress();
    };

    updateChannelUrl = (channelNumber: string, url: string) => {
        return Services.getInstanceOf(ChannelsService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelsService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            channelLogoBaseUrl:
                ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ChannelLogoImageHost],
        }).updateChannelUrl(channelNumber, url);
    };

    fetchOttOfferActionDetails = (
        actionDetailsParams: TActionDetailsParams,
    ): Promise<TActionDetailsList> => {
        return new Promise((resolve, reject) => {
            const actionDetailsService = Services.getInstanceOf(ActionDetailsService, {
                accessToken: DeviceModel.getAccessToken(),
                basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ActionDetailsService],
                bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            });
            actionDetailsService
                .fetchOfferActionDetails(actionDetailsParams)
                .then((data) => {
                    this._validateActionDetailsServiceResponse(data);
                    resolve(data);
                })
                .catch((error: any) => {
                    let actionDetailsError = error;
                    if (
                        !(
                            error instanceof EmptyResponseError ||
                            error instanceof PartialResponseError
                        )
                    ) {
                        const actionDetailsServiceError = error?.response?.data;
                        actionDetailsError = new OfferActionDetailsError(
                            actionDetailsServiceError?.code,
                            actionDetailsServiceError?.message,
                            this.formatSocuErrorDescription(
                                Locale.getString(LocaleKeys.SOCU_ERROR_OVERLAY_DESCRIPTION),
                            ),
                        );
                    }
                    reject(actionDetailsError);
                });
        });
    };

    setSocuLastViewedPosition = (socuLVPParams: TSocuLastViewedPositionParams): Promise<void> => {
        return Services.getInstanceOf(VodSocuLastViewedPositionService, {
            accessToken: DeviceModel.getAccessToken(),
            basePath:
                ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.VodSocuLastViewedPositionService],
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
        }).setSocuLastViewedPosition(socuLVPParams);
    };

    trackNetworkChange = async () => {
        let ips: TIpAddress | null = null;
        try {
            ips = await this.getMyWanIpAddress();
        } catch (error) {
            Log.e(ServicesClientImpl.TAG, 'trackNetworkChange', error);
        }

        let isNetWorkChanged = !ips;
        if (ips) {
            isNetWorkChanged = isNetWorkChanged || ips.newWanIp !== ips.oldWanIp;
            isNetWorkChanged = isNetWorkChanged || ips.newWanIp === '';
            isNetWorkChanged = isNetWorkChanged || ips.oldWanIp === '';
        }

        if (isNetWorkChanged) {
            this.fetchAndStoreInHomeLocation().catch((error) => {
                Log.e(
                    ServicesClientImpl.TAG,
                    'trackNetworkChange::fetchAndStoreInHomeLocation',
                    error,
                );
            });
        }
    };
}
